import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { NotePencil, PencilSimple, Trash, Check, X, CircleNotch } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MIN_H = 180;
const H_KEY = "strata:salary-notes-height";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

function useResizableHeight(defaultH) {
  const [h, setH] = useState(() => Number(sessionStorage.getItem(H_KEY)) || null);
  const height = Math.max(MIN_H, h || defaultH || 320);
  const save = (v) => { const n = Math.max(MIN_H, Math.round(v)); setH(n); sessionStorage.setItem(H_KEY, String(n)); };
  const startDrag = (e) => {
    e.preventDefault();
    const y0 = e.clientY; const h0 = height;
    const move = (ev) => save(h0 + ev.clientY - y0);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); save(height + 32); }
    if (e.key === "ArrowUp") { e.preventDefault(); save(height - 32); }
  };
  return { height, startDrag, onKey };
}

function NoteItem({ n, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(n.body);
  return (
    <li className="group px-3 py-2 border-b border-slate-100 last:border-b-0" data-testid={`salary-note-${n.id}`}>
      {editing ? (
        <div className="space-y-1.5">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} className="text-xs" data-testid={`salary-note-edit-input-${n.id}`} />
          <div className="flex gap-1.5">
            <Button size="sm" className="h-7 text-xs bg-[#327de3] hover:bg-[#1f64c2] text-white" disabled={!text.trim()} onClick={async () => { if (await onSave(n, text)) setEditing(false); }} data-testid={`salary-note-edit-save-${n.id}`}><Check size={12} className="mr-1" /> Save</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEditing(false); setText(n.body); }}><X size={12} className="mr-1" /> Cancel</Button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-xs text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid={`salary-note-body-${n.id}`}>{n.body}</div>
          <div className="mt-1 flex items-center gap-2 text-[10px] text-slate-500">
            <span className="font-semibold text-slate-600" data-testid={`salary-note-author-${n.id}`}>{n.created_by?.name}</span>
            <span data-testid={`salary-note-time-${n.id}`}>{fmt(n.created_at)}</span>
            {n.updated_at && <span className="italic">· edited {fmt(n.updated_at)}{n.updated_by?.name ? ` by ${n.updated_by.name}` : ""}</span>}
            <span className="ml-auto flex gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
              <button type="button" onClick={() => setEditing(true)} className="p-1 rounded hover:bg-slate-100" title="Edit" data-testid={`salary-note-edit-${n.id}`}><PencilSimple size={12} /></button>
              <button type="button" onClick={() => onDelete(n)} className="p-1 rounded hover:bg-rose-50 text-rose-500" title="Delete" data-testid={`salary-note-delete-${n.id}`}><Trash size={12} /></button>
            </span>
          </div>
        </>
      )}
    </li>
  );
}

export function SalaryNotesPanel({ unlockToken, defaultHeight }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const { height, startDrag, onKey } = useResizableHeight(defaultHeight);
  const cfg = { headers: { "X-Salary-Unlock": unlockToken } };
  const listRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/salaries/notes", cfg); setNotes(data); } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  useEffect(() => { if (unlockToken) load(); }, [unlockToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/salaries/notes", { body: text }, cfg); setNotes((l) => [data, ...l]); setText(""); listRef.current?.scrollTo({ top: 0 }); toast.success("Note added"); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not add note")); } finally { setBusy(false); }
  };
  const save = async (n, body) => {
    try { const { data } = await api.put(`/admin/salaries/notes/${n.id}`, { body }, cfg); setNotes((l) => l.map((x) => (x.id === n.id ? data : x))); toast.success("Note updated"); return true; }
    catch (e) { toast.error(apiErrorMessage(e, "Could not update note")); return false; }
  };
  const remove = async () => {
    const n = toDelete; setToDelete(null);
    try { await api.delete(`/admin/salaries/notes/${n.id}`, cfg); setNotes((l) => l.filter((x) => x.id !== n.id)); toast.success("Note deleted"); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not delete note")); }
  };

  return (
    <div className="relative bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden" style={{ height }} data-testid="salary-notes-panel">
      <div className="px-4 pt-3 pb-2 border-b border-slate-100 flex items-center gap-2">
        <NotePencil size={16} className="text-[#327de3]" />
        <h3 className="text-sm font-semibold text-slate-900">Notes</h3>
        <span className="text-[10px] text-slate-400">{notes.length} · newest first</span>
      </div>
      <div className="px-3 py-2 border-b border-slate-100 flex gap-2 items-start">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) add(); }}
          placeholder="Add a note — e.g. reason for a salary adjustment" className="text-xs min-h-[44px]" data-testid="salary-note-input" />
        <Button size="sm" onClick={add} disabled={busy || !text.trim()} className="h-[44px] bg-[#327de3] hover:bg-[#1f64c2] text-white whitespace-nowrap" data-testid="salary-note-add-btn">
          {busy ? <CircleNotch size={14} className="animate-spin" /> : "Add Note"}
        </Button>
      </div>
      <ul ref={listRef} className="flex-1 min-h-0 overflow-y-auto" data-testid="salary-notes-list">
        {notes.length === 0 && <li className="px-4 py-6 text-center text-xs text-slate-400" data-testid="salary-notes-empty">No notes yet.</li>}
        {notes.map((n) => <NoteItem key={n.id} n={n} onSave={save} onDelete={setToDelete} />)}
      </ul>
      <button
        type="button"
        onPointerDown={startDrag}
        onKeyDown={onKey}
        aria-label="Resize notes panel (use arrow up/down)"
        className="absolute bottom-0 right-0 w-4 h-4 cursor-ns-resize text-slate-400 hover:text-[#327de3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#327de3] touch-none"
        data-testid="salary-notes-resize-grip"
      >
        <svg viewBox="0 0 16 16" className="w-4 h-4" aria-hidden="true">
          <path d="M15 5 L5 15 M15 9 L9 15 M15 13 L13 15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="salary-note-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>This salary note will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="salary-note-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="salary-note-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
